import { Box, TextField, Typography, useTheme } from '@mui/material';
import { FormikProps } from 'formik';
import React, { useMemo } from 'react';

import { COVER_IMAGE, SELECTED_DEPARTMENT_IDS, TEMPLATE_NAME } from '../../../../../constants/templates';
import { IMAGE_TYPES } from '../../../../../constants/upload';
import { IClaimLinkBuilderValues, TTemplateMediaFieldName } from '../../../../../types/templates';
import { DepartmentSelect, FormLabel, ImageUploader } from '../../index';

interface IProps {
  formik: FormikProps<IClaimLinkBuilderValues>;
  handleDepartmentChange: (fieldName: keyof IClaimLinkBuilderValues) => (value: string[]) => void;
  handleFileUpload: (fieldName: TTemplateMediaFieldName) => (acceptedFiles: File[]) => Promise<void>;
  handleFileRemove: (fieldName: TTemplateMediaFieldName) => () => void;
}

export const DetailsStep: React.FC<IProps> = ({
  formik,
  handleDepartmentChange,
  handleFileUpload,
  handleFileRemove,
}) => {
  const theme = useTheme();

  const { templateName, selectedDepartmentIds, coverImageUrl } = formik.values;
  const { handleChange, handleBlur, touched, errors } = formik;

  const coverImageUploaderConfig = useMemo(() => {
    return {
      title: 'Click to upload or drag an image',
      helperText: 'PNG or JPG (max. 3MB)',
      accept: IMAGE_TYPES,
      maxSize: 3 * 1024 * 1024, // 3MB in bytes
      height: 188,
      fileUrl: coverImageUrl || undefined,
      onDrop: handleFileUpload(COVER_IMAGE),
      onRemove: handleFileRemove(COVER_IMAGE),
      dataAction: 'cover_image',
    };
  }, [coverImageUrl, handleFileUpload, handleFileRemove]);

  return (
    <>
      <TextField
        id="template-name"
        name={TEMPLATE_NAME}
        label="Template name"
        variant="outlined"
        size="small"
        fullWidth
        value={templateName}
        onChange={handleChange}
        onBlur={handleBlur}
        error={touched.templateName && Boolean(errors.templateName)}
        helperText={touched.templateName && errors.templateName}
        data-action="template_name_input"
        sx={{ mb: 1 }}
      />
      <Box>
        <DepartmentSelect
          value={selectedDepartmentIds || []}
          onChange={handleDepartmentChange(SELECTED_DEPARTMENT_IDS)}
          label="Departments"
          placeholder="Select departments"
          data-action="departments_select"
          disabled={formik.values.is_default}
          tooltip={
            formik.values.is_default ? "Organization default template can't be assigned to departments" : undefined
          }
        />
        <Typography variant="caption" color={theme.palette.text.secondary} sx={{ pt: 0.5, pl: 1.75, display: 'block' }}>
          Select departments that can use this template
        </Typography>
      </Box>
      <FormLabel label="Template cover image" sx={{ py: 0 }}>
        <ImageUploader {...coverImageUploaderConfig} />
      </FormLabel>
    </>
  );
};
